import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2, ExternalLink, X, Bookmark, GitCompareArrows } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SaveScenarioDialog
 *
 * Persists the simulator's current inputs as a named clone estimate. The
 * backend re-runs the simulator to capture the canonical `applied` block,
 * bakes any component_overrides onto `selected_components[*].hour_override`,
 * and sets `scenario_of = source_id` so the clone flows into Compare and
 * History pickers automatically.
 *
 * Props:
 *   open, onOpenChange
 *   sourceEstimateName: string           — shown as "based on {name}"
 *   simulatorRequest: {                   — the exact body we sent to /simulator/run
 *     estimate_id, mode, component_overrides?, component_delta_pct?,
 *     modifier_overrides?, target_weeks?, flex_lever?
 *   }
 *   onSaved(result)                       — called with { id, estimate_name, source_estimate_id, ... }
 */
export default function SaveScenarioDialog({
  open, onOpenChange, sourceEstimateName, simulatorRequest, onSaved,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Auto-suggest a descriptive name from the simulator inputs.
      const req = simulatorRequest || {};
      if (req.mode === 'timeline') {
        const flex = req.flex_lever === 'team' ? 'team' : 'scope';
        setName(`${req.target_weeks} wk · flex ${flex}`);
      } else if (req.mode === 'effort') {
        const n = Object.keys(req.component_overrides || {}).length
          + Object.keys(req.component_delta_pct || {}).length;
        setName(`Effort tweak (${n} component${n === 1 ? '' : 's'})`);
      } else {
        setName('');
      }
      setDescription('');
    }
  }, [open, simulatorRequest]);

  const handleSubmit = async () => {
    if (!simulatorRequest?.estimate_id) {
      toast.error('No estimate selected');
      return;
    }
    if (!name.trim()) {
      toast.error('Give the scenario a name');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/simulator/save-scenario`, {
        name: name.trim(),
        description,
        source: simulatorRequest,
      }, { withCredentials: true });
      toast.success(res.data?.message || 'Scenario saved');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save scenario');
    } finally {
      setSubmitting(false);
    }
  };

  const previewLines = (() => {
    const req = simulatorRequest || {};
    const lines = [];
    if (req.mode === 'timeline') {
      lines.push(`Mode: timeline · flex ${req.flex_lever}`);
      if (req.target_weeks) lines.push(`Target: ${req.target_weeks} weeks`);
    } else if (req.mode === 'effort') {
      lines.push('Mode: effort');
      const abs = Object.entries(req.component_overrides || {});
      const pct = Object.entries(req.component_delta_pct || {});
      if (abs.length) lines.push(`${abs.length} absolute hour override${abs.length === 1 ? '' : 's'}`);
      if (pct.length) lines.push(`${pct.length} percentage delta${pct.length === 1 ? '' : 's'}`);
    }
    return lines;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-scenario-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-slate-700" /> Save as scenario
          </DialogTitle>
          <DialogDescription>
            Persists the current simulator inputs as a named clone of{' '}
            <strong>{sourceEstimateName || 'this estimate'}</strong>. It shows up
            automatically in the Compare and History pickers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="scenario-name" className="text-xs font-medium text-slate-700">Scenario name *</Label>
            <Input
              id="scenario-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Compressed plan (team)"
              className="mt-1"
              data-testid="save-scenario-name"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="scenario-description" className="text-xs font-medium text-slate-700">Description (optional)</Label>
            <Textarea
              id="scenario-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why this scenario — context for your team"
              rows={2}
              className="mt-1"
              data-testid="save-scenario-description"
            />
          </div>

          {previewLines.length > 0 && (
            <div className="bg-slate-50 rounded-md border border-slate-200 p-3 text-[11px] text-slate-600 space-y-0.5" data-testid="save-scenario-preview">
              <div className="font-medium text-slate-700 uppercase tracking-wide text-[10px] mb-1">What will be saved</div>
              {previewLines.map((l, i) => <div key={i}>· {l}</div>)}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="save-scenario-cancel-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !name.trim()} data-testid="save-scenario-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {submitting ? 'Saving…' : 'Save scenario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SavedScenariosCard — list of saved scenarios for the currently-selected
 * estimate on the Simulator page. Collapses to nothing when there are none.
 */
export function SavedScenariosCard({ estimateId, reloadKey, onOpenCompare, onCompareAll }) {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!estimateId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/estimates/${estimateId}/scenarios`, { withCredentials: true })
      .then((res) => setScenarios(res.data?.scenarios || []))
      .catch(() => setScenarios([]))
      .finally(() => setLoading(false));
  }, [estimateId, reloadKey]);

  if (loading || scenarios.length === 0) return null;

  const handleCompareAll = () => {
    const ids = [estimateId, ...scenarios.map((s) => s.id)];
    onCompareAll?.(ids);
  };

  return (
    <div className="mt-3" data-testid="saved-scenarios-card">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
          Saved scenarios ({scenarios.length})
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-[11px] px-2"
          onClick={handleCompareAll}
          data-testid="compare-all-scenarios-btn"
          title="Open the source estimate alongside every saved scenario in Compare"
        >
          <GitCompareArrows className="w-3 h-3 mr-1" />
          Compare all
        </Button>
      </div>
      <div className="space-y-1.5">
        {scenarios.map((s) => {
          const cfg = s.scenario_config || {};
          return (
            <div
              key={s.id}
              className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5 bg-white hover:border-slate-400 transition"
              data-testid={`saved-scenario-${s.id}`}
            >
              <Bookmark className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-slate-800 truncate">
                  {cfg.name || s.estimate_name}
                </div>
                <div className="text-[10px] text-slate-500">
                  {cfg.mode === 'timeline'
                    ? `Timeline · ${cfg.flex_lever} · ${cfg.target_weeks} wk`
                    : `Effort · ${Object.keys(cfg.component_overrides || {}).length} override${Object.keys(cfg.component_overrides || {}).length === 1 ? '' : 's'}`}
                  {' · '}${Math.round(s.total_cost || 0).toLocaleString()}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => onOpenCompare?.(s.id, estimateId)}
                title="Compare vs source"
                data-testid={`saved-scenario-compare-${s.id}`}
              >
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
